import React, { useState } from 'react';
import { FaPlus } from 'react-icons/fa6';
import Input from 'components/Common/Input';
import Radio from 'components/Common/Radio';
import Button from 'components/Common/Button';
import { MdDelete, MdMic } from 'react-icons/md';
import TelInput from 'components/Common/TelInput';
import { IoChevronDownOutline } from 'react-icons/io5';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTalks } from 'apis/business/agent/talks/hook';
import { useAgent } from 'providers/context/AgentProvider';
import { a11yProps } from 'components/Common/CustomTabPanel';
import { TalksRequest } from 'apis/business/agent/talks/types';
import {
  Modal,
  Box,
  Typography,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Accordion,
  Tabs,
  Tab
} from '@mui/material';

interface TalkButtonProps {
  isCollapsed?: boolean;
  showLabel?: boolean;
}

export default function TalkButton({ isCollapsed = false }: TalkButtonProps) {
  const [isTalkModalOpen, setIsTalkModalOpen] = React.useState(false);
  const [value, setValue] = useState<number>(0);
  const { selectedAgent } = useAgent();
  const { mutateAsync: createTalks, isPending } = useTalks();
  const { control, handleSubmit, reset, watch } = useForm<TalksRequest>({
    defaultValues: {
      number_type: 'phone'
    }
  });
  const numberType = watch('number_type');
  const {
    fields: customFields,
    append: fieldAppend,
    remove: fieldRemove
  } = useFieldArray({
    control,
    name: 'custom_variables'
  });

  const handleTalk = async () => {
    const botRoot = document.getElementById('kickCall-chatbot-root');
    if (botRoot) botRoot.remove();
    (window as any).VoiceBot?.open({ agentToken: selectedAgent?.agent_token, isForPreview: true, isTest: true });
  };

  const handleCloseTalkModal = () => {
    reset();
    setIsTalkModalOpen(false);
  };

  const onSubmit = async (data: TalksRequest) => {
    delete data.number_type;
    await createTalks(data);
    handleCloseTalkModal();
  };

  return (
    <>
      <Button
        fullWidth={!isCollapsed}
        size='small'
        onClick={() => setIsTalkModalOpen(true)}
        sx={{
          justifyContent: 'center',
          transition: 'padding 0.3s, justify-content 0.3s',
          minWidth: 'unset'
        }}
      >
        <MdMic style={{ fontSize: 18, marginRight: !isCollapsed ? 8 : 0 }} />
        {!isCollapsed && <Typography>Talk</Typography>}
      </Button>
      <Modal
        open={isTalkModalOpen}
        onClose={() => handleCloseTalkModal()}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box sx={style}>
          <Typography variant='h6' component='h2' mb={1}>
            Talk To {selectedAgent?.name}
          </Typography>
          <Box bgcolor='background.paper' mt={0} mb={2} py={0.5}>
            <Tabs value={value} onChange={(_, newValue) => setValue(newValue)} aria-label='configure tabs'>
              <Tab sx={{ width: '50%' }} label='Phone' {...a11yProps('talk', 'talkpanel', 0)} />
              <Tab sx={{ width: '50%' }} label='Web' {...a11yProps('talk', 'talkpanel', 1)} />
            </Tabs>
          </Box>
          {value === 0 && (
            <Box component='form' onSubmit={handleSubmit(onSubmit)}>
              <Box>
                <Accordion
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #F3F3F3',
                    marginTop: 2,
                    '::before': { background: 'transparent' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                    aria-controls='panel1-content'
                    id='panel1-header'
                  >
                    <Box display='flex' flexDirection='column'>
                      <Typography variant='h6' fontSize={16} fontWeight={600}>
                        Custom Variables
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {customFields.map((field: any, index: number) => (
                      <Box
                        key={field.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          mb: 3
                        }}
                      >
                        <Input
                          name={`custom_variables[${index}].key`}
                          size='small'
                          label='Key'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <Input
                          name={`custom_variables[${index}].value`}
                          size='small'
                          label='Value'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <IconButton size='small' color='error' onClick={() => fieldRemove(index)}>
                          <MdDelete />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      type='button'
                      color='secondary'
                      size='small'
                      variant='outlined'
                      startIcon={<FaPlus />}
                      onClick={() => fieldAppend({ key: '', value: '' })}
                      sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                    >
                      Add Variable
                    </Button>
                  </AccordionDetails>
                </Accordion>
                <Radio
                  name='number_type'
                  control={control}
                  label=''
                  options={[
                    { label: 'Phone', value: 'phone' },
                    { label: 'Extension', value: 'extension' }
                  ]}
                />
                {numberType === 'phone' ? (
                  <TelInput
                    name='to_number'
                    size='small'
                    control={control}
                    placeholder='e.g. +1234567890'
                    defaultCountry='US'
                    preferredCountries={['US', 'CA', 'IN']}
                    fullWidth
                    required
                  />
                ) : (
                  <>
                    <Input name='to_number' size='small' control={control} placeholder='e.g. 401' fullWidth required />
                    <Typography
                      color='#1E66F5'
                      marginTop={2}
                      fontSize={14}
                      bgcolor='#F0F6FF'
                      border='1px solid #D0E3FF'
                      borderRadius={2}
                      padding={1}
                    >
                      Dialing extensions will work if your telephony system is integrated with SIP and has extensions
                      set up for the agent.
                    </Typography>
                  </>
                )}
              </Box>
              <Button type='submit' isPending={isPending} sx={{ mt: 2 }} fullWidth>
                Call Me
              </Button>
            </Box>
          )}

          {value === 1 && (
            <Button
              sx={{ mt: 2 }}
              fullWidth
              onClick={() => {
                handleCloseTalkModal();
                handleTalk();
              }}
            >
              Start Call
            </Button>
          )}
        </Box>
      </Modal>
    </>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  py: 4,
  px: 2
};
